import React from 'react';
import { bool } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  txHasDeliveredPayout,
  txIsCancelResolvedRefunded,
  txIsCanceled,
  txIsCanceledUnresolved,
  txIsDeclined,
} from '../../transactions/transactionProcessBooking';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const LineItemTotalPrice = props => {
  const { transaction, isProvider, intl, isEstimated } = props;

  // Check if transaction is in cancel-unresolved state (pending manual resolution)
  const isPendingCancellation = txIsCanceledUnresolved(transaction);
  
  // Check if transaction was refunded (AUTO_REFUND transition)
  // Note: FULL_PAYOUT also goes to CANCEL_RESOLVED but provider gets full payout
  const isRefundedCancellation = txIsCancelResolvedRefunded(transaction);
  
  // Check final cancelled/declined states (but exclude FULL_PAYOUT which pays provider)
  const isCanceledOrDeclined = 
    txIsCanceled(transaction) ||
    isRefundedCancellation ||
    txIsDeclined(transaction);

  let providerTotalMessageId = 'BookingBreakdown.providerTotalDefault';

  if (txHasDeliveredPayout(transaction)) {
    providerTotalMessageId = 'BookingBreakdown.providerTotalDelivered';
  }

  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : (
    <FormattedMessage id="General.total" />
  );

  // For providers: Check if this is a partial-payment voucher transaction
  // If so, show the manual payout amount instead of the actual payout (which is 0)
  let totalPrice = isProvider
    ? transaction.attributes.payoutTotal
    : transaction.attributes.payinTotal;

  // For final canceled/declined/expired transactions: Show 0 EUR for provider
  // BUT: If transaction went through FULL_PAYOUT, provider gets full payment (not 0)
  if (isProvider && isCanceledOrDeclined) {
    const currency = transaction.attributes.payoutTotal?.currency || transaction.attributes.payinTotal?.currency;
    totalPrice = new Money(0, currency);
  }
  // Check for manual provider payout metadata (partial payment vouchers)
  else if (isProvider) {
    const { providerPayoutAmount, currency } = require('../../util/displayProvider').getProviderDisplayValues(transaction);
    if (typeof providerPayoutAmount === 'number') {
      totalPrice = new Money(providerPayoutAmount, currency);
    } else if (transaction.attributes.payoutTotal?.amount === 0) {
      // Fallback: If payoutTotal is 0 but we don't have metadata, calculate from line items
      // This handles transactions created before the metadata was added
      const lineItems = transaction?.attributes?.lineItems || [];
      const sessionLineItem = lineItems.find(item => 
        item.code && item.code.startsWith('line-item/session') && 
        (!item.includeFor || item.includeFor.includes('provider'))
      );
      
      if (sessionLineItem) {
        totalPrice = sessionLineItem.unitPrice;
      }
    }
  }

  // If cancellation is pending resolution, show pending message instead of amount
  const showPendingMessage = isPendingCancellation;
  const formattedTotalPrice = showPendingMessage 
    ? intl.formatMessage({ id: 'LineItemTotalPrice.pendingCancellationResolution' })
    : formatMoney(intl, totalPrice, true);

  return (
    <>
      {!isEstimated && <hr className={css.totalDivider} />}
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotalPrice}</div>
      </div>
    </>
  );
};

LineItemTotalPrice.propTypes = {
  transaction: propTypes.transaction.isRequired,
  isProvider: bool.isRequired,
  intl: intlShape.isRequired,
};

export default LineItemTotalPrice;
